/* sc-customily-cart.js — Customily cart mutation + drawer bridge.
 *
 * Mirrors sc-customall-custom.js fetch wrapper, but scoped to Customily PDP
 * (product.json with `#cl_optionsapp` present). Kept in a SEPARATE file from
 * Customall: Customily has different DOM (customily-required-label vs
 * ant-form-item-has-error), different SDK (no Vue/Navigation API redirect),
 * and different lifecycle — merging them would bloat conditionals.
 *
 * Bug pattern this fixes (same family as commits 2390b04 + 1bb15ab on Customall):
 *   1. Customily required-fields validation shows `.customily-required-label`
 *      but may NOT always preventDefault on ATC click → shipping-protection.js
 *      microtask fires → orphaned SHP in cart.
 *   2. After a successful ATC, cart-drawer doesn't auto-open on Customily PDP
 *      (no fetch wrapper present), forcing user to click cart icon.
 *   3. Customily's stored-edit PDP replaces the cart line, then redirects to
 *      /cart unless the theme opts into its supported cart-drawer lifecycle.
 *
 * Fix:
 *   - Fetch wrapper intercepts successful cart mutations. Normal adds retain
 *     the existing validation gates.
 *   - Stored edits set `useCartDrawerOnEdit`, wait for Customily's official
 *     `customily-update-cart-item-completed` event, then refresh/open the
 *     drawer only after the old line has been removed.
 *   - Self-gate: early-return if `#cl_optionsapp` missing → zero cost on non-
 *     Customily PDPs (file loaded via sc-pdp-injections.liquid which already
 *     skips Customall templates).
 */
(function () {
  'use strict';

  var CART_ADD_BATCH_MAX_WAIT_MS = 15000;

  function isStoredCustomizationEdit() {
    try {
      var params = new URLSearchParams(window.location.search || '');
      return params.get('isStoredCustomization') === '1'
        && !!params.get('customizationId');
    } catch (error) {
      return false;
    }
  }

  var storedEdit = isStoredCustomizationEdit();
  if (storedEdit) {
    /* Supported Customily integration flag. Without it the vendor redirects
     * every successful stored edit to /cart after replacing the line. */
    enableStoredEditDrawerMode();
    /* Customily's app embed can replace `window.customilyShopify` after this
     * deferred asset first runs. Re-arm the supported flag at the vendor's
     * authoritative ATC boundary so its async `finally` branch cannot fall
     * back to `/cart` with a freshly-created configuration object. */
    window.addEventListener('customily-starts-adding-to-cart', enableStoredEditDrawerMode);
  }

  function enableStoredEditDrawerMode() {
    window.customilyShopify = window.customilyShopify || {};
    window.customilyShopify.useCartDrawerOnEdit = true;
  }

  /* Self-gate: bail if no Customily SDK on page (defense-in-depth; the
   * snippet already excludes Customall templates). */
  function hasCustomily() {
    return !!document.querySelector('#cl_optionsapp, #customily-cart-btn');
  }
  if (storedEdit) {
    /* Install immediately on the official stored-edit URL. Waiting for the
     * async Customily mount can miss its one-shot replacement-complete event,
     * leaving the customer on the PDP with no drawer feedback. */
    install();
  } else if (!hasCustomily()) {
    /* Customily SDK may inject async — poll briefly, bail if absent after 8s. */
    var waited = 0;
    var poll = setInterval(function () {
      waited += 300;
      if (hasCustomily()) { clearInterval(poll); install(); }
      else if (waited >= 8000) clearInterval(poll);
    }, 300);
  } else {
    install();
  }

  function install() {
    var editReplacementCompleted = false;
    var refreshInFlight = false;
    var refreshRequested = false;
    var pendingBundledRefresh = null;
    var pendingFreshRefresh = false;
    var activeCartAddBatch = null;
    var shippingProtectionIntent = null;
    var shippingProtectionIntentRevision = 0;
    /* The refresh chain can keep looping for seconds after an ATC (trailing
     * SHP/upsell mutations re-request it). If the customer dismisses the
     * drawer during that window, the chain-end openCartDrawer() would pop it
     * back open over whatever they are doing (reproduced on LIVE 2026-07-17).
     * A dismissal suppresses only the CURRENT chain's open; a new cart add
     * clears it because the customer expects the drawer for the new item. */
    var suppressChainOpen = false;

    function noteUserDrawerDismiss() {
      if (refreshInFlight || refreshRequested) suppressChainOpen = true;
    }

    document.addEventListener('click', function (event) {
      if (!event.isTrusted || !event.target || !event.target.closest) return;
      var drawer = document.querySelector('cart-drawer');
      if (!drawer || !drawer.classList.contains('active')) return;
      if (event.target.closest('cart-drawer .drawer__close, cart-drawer [id^="CartDrawer"][id*="Close"], #CartDrawer-Overlay, cart-drawer .cart-drawer__overlay')) {
        noteUserDrawerDismiss();
      }
    }, true);

    document.addEventListener('keydown', function (event) {
      if (!event.isTrusted || event.key !== 'Escape') return;
      var drawer = document.querySelector('cart-drawer');
      if (drawer && drawer.classList.contains('active')) noteUserDrawerDismiss();
    }, true);

    /* ---- ATC busy-lock across the Customily submit pipeline ------------
     * Customily generates + uploads the preview BEFORE posting /cart/add
     * (2-6s observed on LIVE) and the vendor button never shows a pending
     * state in that window, so repeated clicks/Enter each start a WHOLE new
     * Customily submit pipeline (new personalization id per POST). Lock the
     * button from the first trusted activation until the add settles.
     * The lock is applied in a MICROTASK so the initial trusted event still
     * reaches Customily's own capture/bubble handlers un-disabled. While
     * locked, further activations are swallowed at capture phase (covers
     * keyboard submit — the form submit event routes through here too).
     * Released on: add settled (success/failure), no add started within
     * NO_SUBMIT_MS (Customily validation blocked the submit — the customer
     * must be able to fix the form and retry), or the safety cap. */
    var ATC_LOCK_SAFETY_MS = 20000;
    var ATC_LOCK_NO_SUBMIT_MS = 6000;
    var atcLockActive = false;
    var atcSafetyTimer = null;
    var atcNoSubmitTimer = null;

    function lockAtcButtons() {
      if (atcLockActive) return;
      atcLockActive = true;
      /* NEVER mutate the ATC button here — not `disabled`, not attributes,
       * not classes. The button is Customily's own state surface
       * (data-valid-fields/ld-over-* live on it) and mutating it while
       * their async pipeline is between awaits resets the pipeline: the
       * submit dies silently (proved on the verify theme 2026-07-17 —
       * trusted click + any button write = zero /cart/add, same click with
       * the button untouched = add fires). Busy state lives on <html>;
       * styling can target the button via .sc-atc-pipeline-busy. The
       * capture-phase swallow in onAtcActivate is what blocks repeats.
       * NOTE: no validation gate here — `.customily-required-label` markers
       * render permanently on some products (proved 2026-07-17: gate made
       * the busy visual never show while the add still fired), and Customily
       * adds with defaults even on an empty form, so a truly blocked submit
       * is rare; the 6s no-submit release covers it. */
      document.documentElement.classList.add('sc-atc-pipeline-busy');
      if (atcSafetyTimer) clearTimeout(atcSafetyTimer);
      atcSafetyTimer = setTimeout(releaseAtcLock, ATC_LOCK_SAFETY_MS);
      if (atcNoSubmitTimer) clearTimeout(atcNoSubmitTimer);
      atcNoSubmitTimer = setTimeout(releaseAtcLock, ATC_LOCK_NO_SUBMIT_MS);
    }

    function noteAtcAddStarted() {
      // The pipeline reached /cart/add — validation passed; hold the lock
      // until the request settles instead of the no-submit fallback.
      if (atcNoSubmitTimer) { clearTimeout(atcNoSubmitTimer); atcNoSubmitTimer = null; }
    }

    function releaseAtcLock() {
      if (atcSafetyTimer) { clearTimeout(atcSafetyTimer); atcSafetyTimer = null; }
      if (atcNoSubmitTimer) { clearTimeout(atcNoSubmitTimer); atcNoSubmitTimer = null; }
      if (!atcLockActive) return;
      atcLockActive = false;
      document.documentElement.classList.remove('sc-atc-pipeline-busy');
    }

    /* Only CLICK and Enter-KEYDOWN are guarded — never the submit event.
     * Customily's pipeline finishes by programmatically clicking the button;
     * that synthetic (untrusted) click makes the UA fire a TRUSTED submit
     * seconds after the user's original activation, and swallowing submits
     * while locked killed exactly that final step (proved by bisect on the
     * verify theme 2026-07-17: every lock variant that guarded `submit`
     * produced zero /cart/add; the identical flow without the submit guard
     * adds fine). Click + Enter cover every USER-initiated repeat. */
    function onAtcActivate(event) {
      if (!event.isTrusted) return;
      if (event.type === 'click') {
        var btn = event.target && event.target.closest && event.target.closest('button[name="add"], [type="submit"][name="add"], #preview-cart-btn');
        if (!btn) return;
        // #preview-cart-btn (nút ATC trong preview modal Customily) nằm NGOÀI
        // product form nhưng chạy cùng pipeline — vẫn phải khoá.
        if (btn.id !== 'preview-cart-btn' && !btn.closest('form[action*="/cart/add"]')) return;
      } else {
        // keydown: only Enter pressed inside the ATC form
        if (event.key !== 'Enter') return;
        var host = event.target && event.target.closest && event.target.closest('form[action*="/cart/add"]');
        if (!host) return;
      }
      if (atcLockActive) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      /* Engage in a MACROTASK so the lock can never affect anything from
       * this same activation's task (its own submit dispatch included).
       * Genuine repeat activations are separate tasks and get swallowed. */
      setTimeout(lockAtcButtons, 0);
    }

    document.addEventListener('click', onAtcActivate, true);
    document.addEventListener('keydown', onAtcActivate, true);

    if (storedEdit) {
      /* Axe's checkbox is mounted asynchronously and does not remove an
       * existing protection line when a Customily cart item is edited. Track
       * only an explicit customer change; an untouched unchecked control must
       * not remove a protection line belonging to the rest of the order. */
      document.addEventListener('change', function (event) {
        if (isShippingProtectionCheckboxChange(event)) {
          shippingProtectionIntent = !!event.target.checked;
          shippingProtectionIntentRevision += 1;
        }
      }, true);

      window.addEventListener('customily-update-cart-item-completed', function () {
        /* Keep the flag true through the vendor completion/finally boundary. */
        enableStoredEditDrawerMode();
        reconcileDeselectedShippingProtection().then(function () {
          editReplacementCompleted = true;
          queueCartDrawerRefresh();
        }).catch(function (error) {
          reportFallback('Stored edit reconciliation failed', error);
          redirectToCart();
        });
      });
    }

    function isShippingProtectionCheckboxChange(event) {
      if (!event || event.isTrusted !== true) return false;
      var target = event.target;
      if (!target || target.type !== 'checkbox') return false;
      if (typeof target.matches !== 'function'
          || !target.matches('#sc-axe-slot .axe-input')) return false;
      var owner = typeof target.closest === 'function'
        ? target.closest('[class*="axe-checkbox" i]')
        : null;
      return !!owner && /shipping|protection|insurance/i.test(owner.textContent || '');
    }

    /* Customily-specific error gate. Check `.customily-required-label` inside
     * `#cl_optionsapp` only when visible (Customily hides them by default and
     * reveals on invalid submit). Include generic ARIA + error classes too. */
    function customilyAtcWillSucceed() {
      var root = document.querySelector('#cl_optionsapp');
      if (!root) return true;
      var selectors = [
        '.customily-required-label',
        '.customily-option.has-error',
        '.customily-field-error',
        '[aria-invalid="true"]'
      ];
      for (var s = 0; s < selectors.length; s++) {
        var nodes = root.querySelectorAll(selectors[s]);
        for (var i = 0; i < nodes.length; i++) {
          if (isVisible(nodes[i])) return false;
        }
      }
      return true;
    }

    function isVisible(el) {
      if (!el) return false;
      if (el.offsetParent === null && getComputedStyle(el).position !== 'fixed') return false;
      var cs = getComputedStyle(el);
      return cs.display !== 'none' && cs.visibility !== 'hidden' && parseFloat(cs.opacity || '1') > 0;
    }

    var _fetch = window.fetch;
    window.fetch = function (resource, init) {
      var url = typeof resource === 'string' ? resource
              : (resource instanceof Request ? resource.url : '');
      var method = ((init && init.method)
        || (resource instanceof Request && resource.method)
        || 'GET').toUpperCase();
      var isCartMutation = method === 'POST'
        && /\/cart\/(add|change|update)(\.js)?(\?|$)/.test(url);
      var isCartAdd = method === 'POST' && /\/cart\/add(\.js)?(\?|$)/.test(url);
      var bundledAdd = isCartAdd ? prepareBundledCustomilyAdd(resource, init) : null;
      var addBatch = isCartAdd ? beginCartAddBatch() : null;
      var promise;
      try {
        promise = bundledAdd
          ? _fetch.call(this, bundledAdd.resource, bundledAdd.init)
          : _fetch.apply(this, arguments);
      } catch (error) {
        finishCartAddBatch(addBatch);
        throw error;
      }
      if (isCartMutation) {
        promise.then(function (res) {
          if (!res.ok) return;
          /* Customily first adds the replacement and only then removes the old
           * line. Do not render that transient duplicate state. The vendor
           * completion event below is the authoritative replacement boundary. */
          if (storedEdit) {
            if (editReplacementCompleted) queueCartDrawerRefresh();
            return;
          }
          if (!isCartAdd) return;
          /* Drawer gate — keep drawer closed if Customily form still has
           * visible validation markers (e.g., required labels showing) OR
           * the shared gate from shipping-protection.js reports invalid.
           * Without this, an SHP POST succeeding while main product was
           * blocked would pop an empty/stale drawer. */
          if (!customilyAtcWillSucceed()) return;
          if (typeof window.scMainAtcWillSucceed === 'function'
              && !window.scMainAtcWillSucceed()) return;
          var bundledRefresh = bundledAdd && typeof res.clone === 'function'
            ? { response: res.clone(), sectionIds: bundledAdd.sectionIds }
            : null;
          requestCartAddRefresh(addBatch, bundledRefresh);
        }).catch(function () {});
      }
      if (isCartAdd) {
        noteAtcAddStarted();
        promise.then(releaseAtcLock, releaseAtcLock);
      }
      if (addBatch) {
        promise.then(function () {
          finishCartAddBatch(addBatch);
        }, function () {
          finishCartAddBatch(addBatch);
        });
      }
      return promise;
    };

    function beginCartAddBatch() {
      if (!activeCartAddBatch) {
        activeCartAddBatch = {
          pending: 0,
          overlapped: false,
          refreshRequested: false,
          forceFresh: false,
          bundledRefresh: null,
          expired: false,
          timeoutId: null
        };
        var batch = activeCartAddBatch;
        batch.timeoutId = setTimeout(function () {
          expireCartAddBatch(batch);
        }, CART_ADD_BATCH_MAX_WAIT_MS);
      } else if (activeCartAddBatch.pending > 0) {
        activeCartAddBatch.overlapped = true;
        activeCartAddBatch.forceFresh = true;
        activeCartAddBatch.bundledRefresh = null;
      }
      activeCartAddBatch.pending += 1;
      return activeCartAddBatch;
    }

    function requestCartAddRefresh(batch, bundledRefresh) {
      // A NEW cart add is fresh intent to see the drawer — it overrides any
      // dismissal recorded against the still-running previous chain.
      suppressChainOpen = false;
      if (!batch) {
        queueCartDrawerRefresh(bundledRefresh);
        return;
      }
      if (batch.expired) {
        /* The response arrived after another request held its original batch
         * open past the safety boundary. Its bundled HTML can be older than a
         * newer add, so reconcile from Shopify's canonical state immediately. */
        queueCartDrawerRefresh(null, true);
        return;
      }
      batch.refreshRequested = true;
      if (batch.overlapped || !bundledRefresh) {
        batch.forceFresh = true;
        batch.bundledRefresh = null;
      } else if (!batch.forceFresh) {
        batch.bundledRefresh = bundledRefresh;
      }
    }

    function finishCartAddBatch(batch) {
      if (!batch || batch.pending <= 0) return;
      batch.pending -= 1;
      if (batch.pending !== 0) return;
      if (batch.timeoutId !== null) {
        clearTimeout(batch.timeoutId);
        batch.timeoutId = null;
      }
      if (batch.expired) return;
      if (activeCartAddBatch === batch) activeCartAddBatch = null;
      if (!batch.refreshRequested) return;
      queueCartDrawerRefresh(
        batch.forceFresh ? null : batch.bundledRefresh,
        batch.forceFresh
      );
    }

    function expireCartAddBatch(batch) {
      if (!batch || batch.pending <= 0 || activeCartAddBatch !== batch) return;

      activeCartAddBatch = null;
      batch.expired = true;
      batch.timeoutId = null;
      batch.overlapped = true;
      batch.forceFresh = true;
      batch.bundledRefresh = null;

      /* At least one add in the batch already succeeded, but an unrelated add
       * never settled. Do not keep the successful customer action invisible.
       * This only releases drawer hydration; it never retries or aborts a cart
       * mutation. Any late successful response requests another fresh pass. */
      if (batch.refreshRequested) {
        batch.refreshRequested = false;
        queueCartDrawerRefresh(null, true);
      }
    }

    function prepareBundledCustomilyAdd(resource, init) {
      if (storedEdit || !init) return null;

      var drawer = document.querySelector('cart-drawer');
      var sectionIds = getDrawerSectionIds(drawer);
      if (!sectionIds.length) return null;

      /* Current Customily Unified submits the product form as FormData and
       * already includes Shopify's `sections`/`sections_url` fields. Reuse
       * that bundled response without cloning or rewriting the multipart
       * body (which would risk invalidating its browser-generated boundary).
       * Keep the JSON branch for older Unified builds. */
      if (isFormLikeBody(init.body)) {
        if (!hasMainCustomilyFormFields(init.body)
            || !hasEveryBundledSection(init.body, sectionIds)) return null;
        return {
          resource: resource,
          init: init,
          sectionIds: sectionIds
        };
      }

      if (typeof init.body !== 'string') return null;

      var payload;
      try {
        payload = JSON.parse(init.body);
      } catch (error) {
        return null;
      }
      if (!payload || !Array.isArray(payload.items)
          || !payload.items.some(isCustomilyCartItem)) return null;

      payload.sections = sectionIds;
      payload.sections_url = window.location.pathname || '/';
      return {
        resource: resource,
        init: Object.assign({}, init, { body: JSON.stringify(payload) }),
        sectionIds: sectionIds
      };
    }

    function isFormLikeBody(body) {
      return (typeof FormData !== 'undefined' && body instanceof FormData)
        || (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams);
    }

    function hasMainCustomilyFormFields(body) {
      var found = false;
      body.forEach(function (_value, key) {
        if (/^properties\[[^\]]*customily/i.test(String(key || ''))
            && !/customily[-_]upsell/i.test(String(key || ''))) {
          found = true;
        }
      });
      return found;
    }

    function hasEveryBundledSection(body, sectionIds) {
      var submitted = [];
      if (typeof body.getAll === 'function') {
        body.getAll('sections').forEach(function (value) {
          if (typeof value !== 'string') return;
          value.split(',').forEach(function (id) {
            id = id.trim();
            if (id) submitted.push(id);
          });
        });
      }
      var sectionsUrl = typeof body.get === 'function' ? body.get('sections_url') : null;
      return typeof sectionsUrl === 'string' && sectionsUrl.length > 0
        && sectionIds.every(function (id) { return submitted.indexOf(id) !== -1; });
    }

    function isCustomilyCartItem(item) {
      var properties = item && item.properties;
      if (!properties || typeof properties !== 'object') return false;
      return Object.keys(properties).some(function (key) {
        return /customily/i.test(key) && !/customily[-_]upsell/i.test(key);
      });
    }

    /* Coalesce mutations that finish together, but repeat the section fetch
     * if Shipping Protection/MagSafe changes settle while one refresh is in
     * flight. This avoids both stale auxiliary lines and arbitrary sleeps. */
    function queueCartDrawerRefresh(bundledRefresh, forceFresh) {
      if (forceFresh || !bundledRefresh) {
        pendingFreshRefresh = true;
        pendingBundledRefresh = null;
      } else if (!pendingFreshRefresh) {
        pendingBundledRefresh = bundledRefresh;
      }
      refreshRequested = true;
      if (refreshInFlight) return;
      refreshInFlight = true;

      Promise.resolve().then(function refreshUntilSettled() {
        refreshRequested = false;
        var bundled = pendingFreshRefresh ? null : pendingBundledRefresh;
        pendingFreshRefresh = false;
        pendingBundledRefresh = null;
        var refresh = bundled
          ? renderBundledCartDrawer(bundled).catch(function () {
              return refreshCartDrawer();
            })
          : refreshCartDrawer();
        return refresh.then(function () {
          if (refreshRequested) return refreshUntilSettled();
        });
      }).then(function () {
        refreshInFlight = false;
        /* A mutation can resolve after the final refresh but before this
         * completion callback. Drain that last request before opening. */
        if (refreshRequested) {
          queueCartDrawerRefresh();
          return;
        }
        var shouldOpen = !suppressChainOpen;
        suppressChainOpen = false;
        if (shouldOpen) openCartDrawer();
      }).catch(function (error) {
        refreshInFlight = false;
        refreshRequested = false;
        pendingFreshRefresh = false;
        pendingBundledRefresh = null;
        suppressChainOpen = false;
        /* The cart mutation already succeeded. If drawer hydration fails,
         * fall back to the locale-aware cart instead of showing stale state
         * or leaving customers to click ATC again and create a duplicate. */
        reportFallback('Cart drawer hydration failed', error);
        redirectToCart();
      });
    }

    /* Fetch updated section HTML via Shopify Section Rendering API; pass to
     * drawer.renderContents(). Toggle `is-empty` manually since renderContents
     * doesn't manage it. */
    function refreshCartDrawer() {
      var drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.renderContents !== 'function') {
        return Promise.reject(new Error('Cart drawer is unavailable'));
      }
      var sectionIds = getDrawerSectionIds(drawer);
      if (!sectionIds.length) return Promise.reject(new Error('Cart drawer sections are unavailable'));

      return Promise.all([
        _fetch(getShopifyRoute('?sections=' + sectionIds.join(','))).then(function (response) {
          if (!response.ok) throw new Error('Cart drawer section request failed');
          return response.json();
        }),
        _fetch(getShopifyRoute('cart.js')).then(function (response) {
          if (!response.ok) throw new Error('Cart request failed');
          return response.json();
        })
      ]).then(function (results) {
        var sections = results[0];
        var cart = results[1];
        var hasEverySection = sections && sectionIds.every(function (id) {
          return typeof sections[id] === 'string' && sections[id].length > 0;
        });
        if (!hasEverySection) throw new Error('Cart drawer section response is incomplete');
        if (!cart || typeof cart.item_count !== 'number') throw new Error('Cart response is invalid');

        /* Shrine's second argument suppresses renderContents' implicit open.
         * Open only after all coalesced SHP/MagSafe mutations have settled. */
        drawer.renderContents({ sections: sections }, true);
        if (cart.item_count > 0) drawer.classList.remove('is-empty');
        else drawer.classList.add('is-empty');
      });
    }

    function renderBundledCartDrawer(bundled) {
      var drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.renderContents !== 'function') {
        return Promise.reject(new Error('Cart drawer is unavailable'));
      }
      return bundled.response.json().then(function (payload) {
        var sections = payload && payload.sections;
        var complete = sections && bundled.sectionIds.every(function (id) {
          return typeof sections[id] === 'string' && sections[id].length > 0;
        });
        if (!complete) throw new Error('Bundled cart drawer response is incomplete');

        drawer.renderContents({ sections: sections }, true);
        drawer.classList.remove('is-empty');
      });
    }

    function getDrawerSectionIds(drawer) {
      if (!drawer) return [];
      var sectionsList = typeof drawer.getSectionsToRender === 'function'
        ? drawer.getSectionsToRender()
        : [{ id: 'cart-drawer' }, { id: 'cart-icon-bubble' }];
      return sectionsList.map(function (section) {
        return section && section.id;
      }).filter(function (id) {
        return typeof id === 'string' && id.length > 0;
      });
    }

    function getShopifyRoute(path) {
      var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
      if (root.charAt(root.length - 1) !== '/') root += '/';
      return root + String(path || '').replace(/^\//, '');
    }

    function reconcileDeselectedShippingProtection() {
      if (shippingProtectionIntent !== false) return Promise.resolve();
      var intentRevision = shippingProtectionIntentRevision;

      return _fetch(getShopifyRoute('cart.js')).then(function (response) {
        if (!response.ok) throw new Error('Cart request failed before protection reconciliation');
        return response.json();
      }).then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) {
          throw new Error('Cart response is invalid before protection reconciliation');
        }
        /* The customer may change the checkbox while cart.js is in flight.
         * Restart against the latest intent instead of applying a stale
         * opt-out decision. Synchronous request dispatch below leaves no
         * interleaving gap after this revision check. */
        if (shippingProtectionIntentRevision !== intentRevision) {
          return reconcileDeselectedShippingProtection();
        }
        if (shippingProtectionIntent !== false) return null;
        var updates = {};
        cart.items.forEach(function (item) {
          var handle = String(item && (item.handle || item.product_handle) || '').toLowerCase();
          if ((handle === 'shipping-protection' || handle === 'shipping-insurance')
              && item && item.key) {
            updates[item.key] = 0;
          }
        });
        if (!Object.keys(updates).length) return null;
        return _fetch(getShopifyRoute('cart/update.js?customily_upsell'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates })
        }).then(function (response) {
          if (!response.ok) throw new Error('Shipping Protection removal failed');
        });
      });
    }

    function redirectToCart() {
      var cartUrl = getShopifyRoute('cart');
      if (window.location && typeof window.location.assign === 'function') {
        window.location.assign(cartUrl);
      } else if (window.location) {
        window.location.href = cartUrl;
      }
    }

    function reportFallback(context, error) {
      if (!window.console || typeof window.console.warn !== 'function') return;
      var detail = error && error.message ? ': ' + error.message : '';
      window.console.warn('[sc-customily-cart] ' + context + detail);
    }

    function openCartDrawer() {
      var drawer = document.querySelector('cart-drawer');
      if (!drawer || drawer.classList.contains('active')) return;
      if (typeof drawer.open === 'function') { drawer.open(); return; }
      if (typeof drawer.show === 'function') { drawer.show(); return; }
      var cartLink = document.querySelector('a.header__icon--cart');
      if (cartLink) { cartLink.click(); return; }
      drawer.classList.add('active');
    }
  }
})();
